import { Table } from '@zoom/components';
import { TFunction } from 'i18next';
import React from 'react';

import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { AdherenceHistoryItem, Team } from '../../graphql/components';
import { nameOf } from '../../utils/helpers';
import { AdherenceHistoryList } from './AdherenceHistoryList';
import { getListHeaderTag, LIST_CONTAINER_TAG } from './constants';

export interface AdherenceHistoryTableProps {
  activeId?: string;
  adherenceHistory: AdherenceHistoryItem[];
  isOneDaySelected?: boolean;
  onViewAdherenceHistoryItem: (h: AdherenceHistoryItem) => void;
  t: TFunction;
  teams: Record<string, Team>;
}

export const AdherenceHistoryTable: React.FC<AdherenceHistoryTableProps> = ({
  adherenceHistory,
  isOneDaySelected,
  onViewAdherenceHistoryItem,
  t,
  activeId,
  teams,
}) => {
  const { userHasPermissions } = useAuth();

  const hasViewPermission = userHasPermissions([Permissions.ViewAdherenceHistory]);

  const hasPermission = hasViewPermission;

  return (
    <Table data-test-id={LIST_CONTAINER_TAG} striped fixedLayout withContainerPadding>
      <thead>
        <tr>
          <th data-test-id={getListHeaderTag(nameOf<AdherenceHistoryItem>('agentSurname'))}>{t('person.name')}</th>
          <th data-test-id={getListHeaderTag(nameOf<AdherenceHistoryItem>('teamIds'))}>
            {t('team.team', { count: 2 })}
          </th>
          <th data-test-id={getListHeaderTag(nameOf<AdherenceHistoryItem>('scheduledTime'))} className="text-center">
            {t('scheduleAdherence.scheduledTime')}
          </th>
          <th
            data-test-id={getListHeaderTag(nameOf<AdherenceHistoryItem>('scheduledWorkingTime'))}
            className="text-center"
          >
            {t('scheduleAdherence.scheduledWorkTime')}
          </th>

          <th
            data-test-id={getListHeaderTag(nameOf<AdherenceHistoryItem>('outOfAdhTimeTotal'))}
            className="text-center"
          >
            {t('scheduleAdherence.timeOutOfAdherence')}
          </th>
          <th
            data-test-id={getListHeaderTag(nameOf<AdherenceHistoryItem>('outOfAdhEventsCount'))}
            className="text-center"
          >
            {t('scheduleAdherence.outOfAdherenceEvents')}
          </th>
          <th
            data-test-id={getListHeaderTag(nameOf<AdherenceHistoryItem>('inAdherencePercentage'))}
            className="text-center"
          >
            {t('scheduleAdherence.inAdherence')}
          </th>
          <th
            data-test-id={getListHeaderTag(nameOf<AdherenceHistoryItem>('conformancePercentage'))}
            className="text-center"
          >
            {t('scheduleAdherence.conformance')}
          </th>
          <th
            data-test-id={getListHeaderTag(nameOf<AdherenceHistoryItem>('lateArrivalsCount'))}
            className="text-center"
          >
            {t('scheduleAdherence.lateArrivals', { count: 2 })}
          </th>
          <th data-test-id={getListHeaderTag(nameOf<AdherenceHistoryItem>('earlyLeavesCount'))} className="text-center">
            {t('scheduleAdherence.earlyLeaves', { count: 2 })}
          </th>
          {hasPermission && <th data-test-id={getListHeaderTag('actions')}>{t('layout.action.actions')}</th>}
        </tr>
      </thead>
      <AdherenceHistoryList
        activeId={activeId}
        adherenceHistory={adherenceHistory}
        hasViewPermission={hasViewPermission}
        hasPermission={hasPermission}
        isOneDaySelected={isOneDaySelected}
        onViewAdherenceHistoryItem={onViewAdherenceHistoryItem}
        teams={teams}
        t={t}
      />
    </Table>
  );
};
